/**
 * @file input.component.tsx
 * @path packages/ui/src/input.component.tsx
 */

import { cn } from "@sirbastians/utils";
import * as React from "react";
import { Label } from "./label.component";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, id, error, placeholder, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;

    return (
      <div className="relative w-full group">
        <input
          type={type}
          id={inputId}
          className={cn(
            // Base Layout
            "peer flex h-12 w-full -mb-2 rounded-xl border px-4 pt-2 text-sm transition-all duration-200 outline-none",

            // NEUROMORPHIC STYLE
            "neuro-well bg-secondary text-secondary-foreground border-input",

            // INTERACTION STATES
            "hover:border-foreground/20",
            "focus:border-muted-foreground focus:ring-1 focus:ring-muted-foreground/20",

            // --- VISIBILITY LOGIC ---
            // 1. Make the placeholder text transparent by default
            "placeholder:text-transparent",
            // 2. Make it visible only when the input is focused
            "focus:placeholder:text-muted-foreground/50",
            // 3. Smooth transition for the placeholder appearance
            "placeholder:transition-colors placeholder:duration-200",

            // ERROR STATE
            error &&
              "border-destructive focus:border-destructive focus:ring-destructive/20",

            // Disabled
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          // Ensure we fall back to a space if no placeholder is provided 
          // to maintain the floating label "resting" state 
          placeholder={placeholder || " "} 
          ref={ref}
          {...props}
        />

        {label && (
          <Label
            htmlFor={inputId}
            className={cn(
              "absolute left-4 transition-all duration-200 ease-out origin-[0] pointer-events-none",

              // --- 1. DEFAULT STATE (Floating / Filled) ---
              "top-0.5 scale-[65%] font-medium text-muted-foreground bg-transparent",

              // --- 2. RESTING STATE (Empty / Placeholder Shown) ---
              "peer-placeholder-shown:top-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-muted-foreground peer-placeholder-shown:font-normal",

              // --- 3. FOCUS STATE (Active) ---
              "peer-focus:top-0.5 peer-focus:scale-[65%] peer-focus:text-primary peer-focus:font-bold",

              error && "text-destructive peer-focus:text-destructive",
            )}
          >
            {label}
          </Label>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };